import { useEffect, useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { supabase } from '@/core/supabase/client';
import { listParticipants, type Participant } from '@/features/works/works';
import { colorDe, COLORES, ColorParticipante } from '@/ui/ColorParticipante';
import { MenuAcciones } from '@/ui/MenuAcciones';
import { EncabezadoSeccion } from '@/ui/Sistema';

/**
 * Administrar los participantes de la organización.
 *
 * Esto no existía, y se descubrió cargando el listado histórico: hacía falta un
 * participante para representar a la veterinaria cobrando en efectivo, y hubo
 * que crearlo con SQL directo contra la base. Para una prueba pasa; para un
 * producto que se vende, que una veterinaria no pueda dar de alta a un socio
 * sin que alguien le corra una consulta no es una opción.
 *
 * Los participantes se creaban solos por dos caminos —la cuenta técnica al
 * crear la organización, y las personas al aceptar una invitación— y ninguno
 * cubre el caso de una parte que **no entra a la aplicación**: la caja de la
 * veterinaria, una sociedad, un tercero que cobra.
 *
 * El COLOR es lo que se ve en el listado de trabajos, y representa **a quién
 * está atribuida la facturación**. No el reparto de ganancia, no la movilidad,
 * no el destinatario externo de la factura. Agosto mostró que las cuatro cosas
 * pueden diferir en un mismo trabajo, así que pintar una y sugerir otra sería
 * peor que no pintar nada.
 */
export function ParticipantesCard({
  organizationId,
  isAdmin,
}: {
  organizationId: string;
  isAdmin: boolean;
}) {
  const [gente, setGente] = useState<Participant[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Qué participante tiene abierto el selector de color. Uno por vez: dos
  // paletas abiertas a la vez no dejan ver de quién es cuál.
  const [pintando, setPintando] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [nuevo, setNuevo] = useState('');
  const [creando, setCreando] = useState(false);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const l = await listParticipants(organizationId);
        if (!cancelado) setGente(l);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [organizationId, recarga]);

  const guardar = async (id: string, patch: Partial<Participant>) => {
    setError(null);
    try {
      const { error: e } = await supabase.from('participants').update(patch).eq('id', id);
      if (e) throw e;
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const agregar = async () => {
    if (nuevo.trim() === '') return;
    setError(null);
    try {
      // `entity`: una parte que cobra pero no entra a la aplicación. Las
      // personas con acceso se crean solas al aceptar la invitación, así que
      // darlas de alta acá duplicaría a alguien que ya va a existir.
      const { error: e } = await supabase
        .from('participants')
        .insert({ organization_id: organizationId, display_name: nuevo.trim(), kind: 'entity' });
      if (e) throw e;
      setNuevo('');
      setCreando(false);
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  return (
    <section className="flex flex-col gap-3" data-testid="participantes-card">
      <EncabezadoSeccion titulo="Participantes" />
      <p className="mb-3 text-sm text-[var(--tinta-suave)]">
        Quiénes se reparten la plata. El color se usa en el listado de trabajos para ver de un
        vistazo <strong>a nombre de quién se factura</strong>.
      </p>

      {error && (
        <p
          role="alert"
          className="mb-2 text-sm text-[var(--peligro)]"
          data-testid="participantes-error"
        >
          {error}
        </p>
      )}

      <ul className="flex flex-col divide-y divide-[var(--linea)]">
        {gente.map((p) => (
          <li
            key={p.id}
            className="flex flex-wrap items-center gap-3 py-3"
            data-testid="participante"
          >
            {/* El punto lleva su color configurado. Es un acento, no la
                identidad: al lado está el nombre, siempre. */}
            <span
              className="size-3 shrink-0 rounded-full border border-[var(--linea-fuerte)]"
              style={{ backgroundColor: colorDe(p.color) ?? 'transparent' }}
              aria-hidden
            />

            <div className="min-w-0 flex-1">
              <input
                defaultValue={p.display_name}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== p.display_name) void guardar(p.id, { display_name: v });
                }}
                // La cuenta técnica SÍ se renombra. Su identidad es el `kind`,
                // que la base usa para balancear los ajustes y absorber lo que
                // queda sin facturador; el nombre visible es sólo cómo la
                // llama cada veterinaria: «Caja», «Veterinaria López».
                disabled={!isAdmin}
                className="w-full border-0 bg-transparent p-0 font-medium text-[var(--tinta)] disabled:opacity-100"
                data-testid="participante-nombre"
              />
              <p className="text-sm text-[var(--tinta-suave)]">
                {p.kind === 'org_account'
                  ? 'Cuenta de la organización'
                  : p.kind === 'entity'
                    ? 'Entidad'
                    : 'Persona'}
                {!p.is_active && ' · inactivo'}
              </p>
            </div>

            {/* Un menú, no una fila de botones debajo de cada nombre. La
                cuenta de la organización no ofrece desactivar —es la que
                balancea los ajustes y absorbe lo que queda sin facturador, y
                sin ella la liquidación no cierra en cero—, pero eso se
                resuelve no ofreciendo la acción, no marcándola como error. */}
            {isAdmin && (
              <MenuAcciones
                etiqueta={`Acciones de ${p.display_name}`}
                testId="participante-menu"
                acciones={[
                  {
                    etiqueta: 'Cambiar color',
                    testId: 'participante-color',
                    onClick: () => setPintando(pintando === p.id ? null : p.id),
                  },
                  ...(p.kind !== 'org_account'
                    ? [
                        {
                          etiqueta: p.is_active ? 'Desactivar' : 'Activar',
                          testId: 'participante-activar',
                          onClick: () => void guardar(p.id, { is_active: !p.is_active }),
                        },
                      ]
                    : []),
                ]}
              />
            )}
            {isAdmin && pintando === p.id && (
              <div className="basis-full pt-2">
                <ColorParticipante
                  valor={p.color ?? null}
                  onChange={(c) => {
                    void guardar(p.id, { color: c });
                    setPintando(null);
                  }}
                />
              </div>
            )}
          </li>
        ))}
      </ul>

      {isAdmin &&
        (creando ? (
          <div className="mt-3 flex flex-col gap-2 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-3">
            <input
              value={nuevo}
              onChange={(e) => setNuevo(e.target.value)}
              placeholder="Nombre. Por ejemplo, la caja de la veterinaria"
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
              data-testid="participante-nuevo-nombre"
            />
            <p className="text-xs text-[var(--tinta-tenue)]">
              Para alguien que va a usar la aplicación, invitalo desde acá arriba: su participante
              se crea solo al aceptar.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void agregar()}
                className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-sm text-[var(--sobre-accion)] "
                data-testid="participante-crear"
              >
                Agregar
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreando(false);
                  setNuevo('');
                }}
                className="min-h-11 px-2 text-sm underline opacity-70"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCreando(true)}
            className="mt-3 min-h-11 w-full rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] border-dashed text-sm"
            data-testid="participante-agregar"
          >
            ＋ Agregar participante
          </button>
        ))}

      <p className="mt-3 text-xs text-[var(--tinta-tenue)]">
        Colores disponibles: {COLORES.map((c) => c.nombre).join(', ')}.
      </p>
    </section>
  );
}
